import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getClients, createClient, patchClient, deleteClient,
  changeClientPassword, changePasswordAdmin
} from '../services/api';

const S = {
  wrap: { minHeight: '100vh', background: '#0f0f14', color: '#e2e8f0', fontFamily: "'Segoe UI', sans-serif" },
  nav: {
    background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.08)',
    padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '60px',
  },
  navBrand: { color: '#a78bfa', fontWeight: 700, fontSize: '18px', letterSpacing: '-0.5px' },
  navRight: { display: 'flex', gap: '12px', alignItems: 'center' },
  btnSm: {
    padding: '7px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.12)',
    background: 'transparent', color: '#94a3b8', fontSize: '13px', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  main: { maxWidth: '1100px', margin: '0 auto', padding: '32px 24px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' },
  h1: { fontSize: '24px', fontWeight: 700, color: '#f1f5f9', margin: 0 },
  btnPrimary: {
    padding: '10px 20px', borderRadius: '10px', border: 'none',
    background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
    color: '#fff', fontWeight: 600, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit',
  },
  grid: { display: 'grid', gap: '16px' },
  card: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '14px', padding: '20px 24px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
  },
  badge: (active) => ({
    padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
    background: active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
    color: active ? '#86efac' : '#fca5a5',
    border: `1px solid ${active ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
  }),
  actions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  btnAct: (variant) => ({
    padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
    fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit',
    background: variant === 'danger' ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.06)',
    color: variant === 'danger' ? '#fca5a5' : '#cbd5e1',
  }),
  modal: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px',
  },
  modalCard: {
    background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '18px', padding: '32px', width: '100%', maxWidth: '440px',
  },
  mTitle: { fontSize: '20px', fontWeight: 700, color: '#f1f5f9', marginBottom: '20px' },
  label: { display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '6px' },
  input: {
    width: '100%', padding: '11px 14px',
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px', color: '#fff', fontSize: '14px',
    marginBottom: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  },
  row: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' },
  err: { color: '#fca5a5', fontSize: '13px', marginBottom: '12px', background: 'rgba(239,68,68,0.1)', padding: '10px', borderRadius: '8px' },
  expiry: (date) => {
    if (!date) return { color: '#64748b', fontSize: '12px' };
    const expired = new Date(date) < new Date();
    return { color: expired ? '#fca5a5' : '#86efac', fontSize: '12px' };
  },
};

const emptyClient = { username: '', password: '', storeName: '', expiresAt: '' };

export default function AdminPage() {
  const { token, signOut } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [modal, setModal]   = useState(null); // 'create' | 'password' | 'mypassword'
  const [form, setForm]     = useState(emptyClient);
  const [pwForm, setPwForm] = useState({ newPassword: '', confirmPassword: '' });
  const [myPwForm, setMyPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [selId, setSelId]   = useState(null);
  const [err, setErr]       = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try { setClients(await getClients(token)); } catch {}
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyClient); setErr(''); setModal('create'); };
  const openPw = (id) => { setSelId(id); setPwForm({ newPassword: '', confirmPassword: '' }); setErr(''); setModal('password'); };
  const openMyPw = () => { setMyPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); setErr(''); setModal('mypassword'); };

  const handleCreate = async () => {
    setErr(''); setLoading(true);
    try {
      await createClient({ username: form.username, password: form.password, storeName: form.storeName, expiresAt: form.expiresAt || null }, token);
      await load(); setModal(null);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  const handleToggle = async (c) => {
    try { await patchClient(c.id, { active: !c.active }, token); await load(); } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este cliente y su tienda? Esto no se puede deshacer.')) return;
    try { await deleteClient(id, token); await load(); } catch {}
  };

  const handlePw = async () => {
    setErr(''); setLoading(true);
    try {
      await changeClientPassword(selId, pwForm, token);
      setModal(null);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  const handleMyPw = async () => {
    setErr(''); setLoading(true);
    try {
      await changePasswordAdmin(myPwForm, token);
      setModal(null);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  return (
    <div style={S.wrap}>
      <nav style={S.nav}>
        <span style={S.navBrand}>🏪 TiendaSaaS — Admin</span>
        <div style={S.navRight}>
          <button style={S.btnSm} onClick={() => navigate('/admin/template')}>Plantilla base</button>
          <button style={S.btnSm} onClick={openMyPw}>Mi contraseña</button>
          <button style={{ ...S.btnSm, color: '#fca5a5' }} onClick={signOut}>Salir</button>
        </div>
      </nav>

      <div style={S.main}>
        <div style={S.header}>
          <h1 style={S.h1}>Clientes ({clients.length})</h1>
          <button style={S.btnPrimary} onClick={openCreate}>+ Nuevo cliente</button>
        </div>

        {clients.length === 0 && (
          <div style={{ textAlign: 'center', color: '#475569', padding: '60px 0', fontSize: '15px' }}>
            No hay clientes todavía. Creá el primero.
          </div>
        )}

        <div style={S.grid}>
          {clients.map(c => (
            <div key={c.id} style={S.card}>
              <div>
                <div style={{ fontWeight: 600, color: '#f1f5f9', marginBottom: '4px' }}>
                  {c.store?.config?.nombre || c.username}
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>@{c.username}</div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={S.badge(c.active)}>{c.active ? 'Activa' : 'Inactiva'}</span>
                  <span style={S.expiry(c.expiresAt)}>
                    {c.expiresAt
                      ? `Vence: ${new Date(c.expiresAt).toLocaleDateString('es-AR')}`
                      : 'Sin vencimiento'}
                  </span>
                  <a
                    href={`/tienda/${c.id}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: '12px', color: '#a78bfa', textDecoration: 'none' }}
                  >
                    Ver tienda ↗
                  </a>
                </div>
              </div>
              <div style={S.actions}>
                <button style={S.btnAct()} onClick={() => handleToggle(c)}>
                  {c.active ? 'Desactivar' : 'Activar'}
                </button>
                <button style={S.btnAct()} onClick={() => openPw(c.id)}>Cambiar clave</button>
                <button
                  style={S.btnAct()}
                  onClick={() => {
                    const d = window.prompt('Nueva fecha de vencimiento (YYYY-MM-DD), dejá vacío para sin vencimiento:');
                    if (d === null) return;
                    patchClient(c.id, { expiresAt: d || null }, token).then(load);
                  }}
                >
                  Vencimiento
                </button>
                <button style={S.btnAct('danger')} onClick={() => handleDelete(c.id)}>Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MODAL CREAR CLIENTE */}
      {modal === 'create' && (
        <div style={S.modal} onClick={() => setModal(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={S.mTitle}>Nuevo cliente</div>
            {err && <div style={S.err}>{err}</div>}
            <label style={S.label}>Usuario *</label>
            <input style={S.input} value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="usuario123" />
            <label style={S.label}>Contraseña *</label>
            <input style={S.input} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="mínimo 6 caracteres" />
            <label style={S.label}>Nombre de la tienda</label>
            <input style={S.input} value={form.storeName} onChange={e => setForm({ ...form, storeName: e.target.value })} placeholder="Mi Tienda" />
            <label style={S.label}>Vencimiento (opcional)</label>
            <input style={S.input} type="date" value={form.expiresAt} onChange={e => setForm({ ...form, expiresAt: e.target.value })} />
            <div style={S.row}>
              <button style={S.btnSm} onClick={() => setModal(null)}>Cancelar</button>
              <button style={{ ...S.btnPrimary, opacity: loading ? 0.7 : 1 }} onClick={handleCreate} disabled={loading}>
                {loading ? 'Creando...' : 'Crear cliente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CAMBIAR CLAVE CLIENTE */}
      {modal === 'password' && (
        <div style={S.modal} onClick={() => setModal(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={S.mTitle}>Cambiar contraseña del cliente</div>
            {err && <div style={S.err}>{err}</div>}
            <label style={S.label}>Nueva contraseña</label>
            <input style={S.input} type="password" value={pwForm.newPassword} onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })} />
            <label style={S.label}>Confirmar contraseña</label>
            <input style={S.input} type="password" value={pwForm.confirmPassword} onChange={e => setPwForm({ ...pwForm, confirmPassword: e.target.value })} />
            <div style={S.row}>
              <button style={S.btnSm} onClick={() => setModal(null)}>Cancelar</button>
              <button style={{ ...S.btnPrimary, opacity: loading ? 0.7 : 1 }} onClick={handlePw} disabled={loading}>
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MI CONTRASEÑA */}
      {modal === 'mypassword' && (
        <div style={S.modal} onClick={() => setModal(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={S.mTitle}>Mi contraseña</div>
            {err && <div style={S.err}>{err}</div>}
            <label style={S.label}>Contraseña actual</label>
            <input style={S.input} type="password" value={myPwForm.currentPassword} onChange={e => setMyPwForm({ ...myPwForm, currentPassword: e.target.value })} />
            <label style={S.label}>Nueva contraseña</label>
            <input style={S.input} type="password" value={myPwForm.newPassword} onChange={e => setMyPwForm({ ...myPwForm, newPassword: e.target.value })} />
            <label style={S.label}>Confirmar</label>
            <input style={S.input} type="password" value={myPwForm.confirmPassword} onChange={e => setMyPwForm({ ...myPwForm, confirmPassword: e.target.value })} />
            <div style={S.row}>
              <button style={S.btnSm} onClick={() => setModal(null)}>Cancelar</button>
              <button style={{ ...S.btnPrimary, opacity: loading ? 0.7 : 1 }} onClick={handleMyPw} disabled={loading}>
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
